<template>
  <!-- Do not use property cache-items in this autocomplete-->
  <!-- it has some conflicts with no-filter prop-->
  <!-- https://github.com/vuetifyjs/vuetify/issues/11365 -->

  <v-autocomplete
    chips
    clearable
    dense
    outlined
    no-filter
    return-object
    item-value="id"
    item-text="title"
    data-testid="assigneeSelector"
    :menu-props="{
      allowOverflow: true, // This prop allow us to be sure of displaying the list of options below the autocomplete. When this props is not passed to v-menu, it auto-position itself if the list of elements is too large, overlaping the area where user is typing the search criteria.
      auto: true, // In order to show all the entries on the list we have to pass this prop https://github.com/vuetifyjs/vuetify/issues/11144#issuecomment-817528155. Unfortunately, this will rollback the following PR's changes https://github.com/reverscore/service-web/pull/2687
      contentClass: 'autocomplete-menu', // The 'auto' prop invalidates the 'max-height' prop we could pass to v-menu. We have to pass a custom classname as a workaround. https://github.com/vuetifyjs/vuetify/issues/11144#issuecomment-859358889
    }"
    class="assignee-selector"
    ref="autocomplete"
    :hide-details="hideDetails"
    :multiple="multiple"
    :hide-selected="!isSearching"
    :disabled="disabled"
    :items="inputItems"
    :label="labelText"
    :loading="isLoading"
    :rules="validationRules || rules"
    :search-input.sync="searchTerm"
    :value="selectedAccountIds"
    @change="handleChange"
  >
    <template v-slot:prepend-inner>
      <slot />
    </template>
    <template v-if="showEmptyState" v-slot:no-data>
      <EmptyState
        data-testid="assigneeSelector-emptyState"
        :icon="emptyStateIcon"
        :title="emptyStateTitle"
        :message="emptyStateMessage"
      />
    </template>
    <template v-slot:selection="data">
      <span v-if="multiple" class="mb-2">
        <ChipAvatar
          v-bind="data.attrs"
          small
          :account="data.item"
          :vuetifyProps="{
            close: !disabled,
            inputValue: data.selected,
            ripple: false,
          }"
          @click="data.select"
          @click:close="handleRemove(data.item)"
        />
      </span>
      <span v-else class="account-single-name">
        {{ getShortenedName(data.item) }}
      </span>
    </template>

    <template v-slot:item="data">
      <v-list-item-avatar class="d-flex justify-center">
        <v-avatar v-if="data.item.isTeam" color="teal lighten-5">
          <v-icon color="teal">
            mdi-account-group
          </v-icon>
        </v-avatar>
        <UserAvatar v-else size="32px" fontSize="10px" :account="data.item" />
      </v-list-item-avatar>

      <v-list-item-content>
        <div class="d-flex justify-space-between align-center">
          <div>
            <v-list-item-title>
              <text-highlight
                highlightClass="yellow lighten-1"
                data-private
                highlightComponent="span"
                :queries="searchTerm || ''"
              >
                {{ data.item.title }}
              </text-highlight>
            </v-list-item-title>
            <v-list-item-subtitle>
              <text-highlight
                highlightClass="yellow lighten-1"
                data-private
                highlightComponent="span"
                :queries="searchTerm || ''"
              >
                {{ data.item.subtitle }}
              </text-highlight>
            </v-list-item-subtitle>
          </div>
          <v-chip v-if="data.item.isTeam" x-small>
            {{ $t('common.team_one') }}
          </v-chip>
        </div>
      </v-list-item-content>
    </template>
  </v-autocomplete>
</template>

<script>
import _ from 'lodash'

import user from '@/mixins/user.vue'

import ChipAvatar from './ChipAvatar.vue'
import UserAvatar from './UserAvatar.vue'
import EmptyState from './EmptyState.vue'

export default {
  props: {
    required: Boolean,
    disabled: Boolean,
    isLoading: Boolean,
    emptyStateIcon: String,
    emptyStateTitle: String,
    emptyStateMessage: String,
    showEmptyState: {
      type: Boolean,
      default: false,
    },
    selectedAccountIds: {
      type: [Array, String],
      required: false,
      default: () => [],
    },
    availableAccounts: {
      type: Array,
      default: () => [],
    },
    availableTeams: {
      type: Array,
      default: () => [],
    },
    initialAssignees: {
      type: Array,
      default: () => [],
    },
    limit: { type: Number, default: null },
    label: {
      type: String,
      required: false,
    },
    validationRules: {
      type: Array,
      required: false,
    },
    multiple: {
      type: Boolean,
      required: false,
    },
    hideDetails: { type: Boolean, required: false, default: false },
  },

  mixins: [user],

  components: {
    UserAvatar,
    ChipAvatar,
    EmptyState,
  },

  data() {
    return {
      searchTerm: '',
      cachedAccounts: {},
      teamsAccounts: [],
    }
  },

  mounted() {
    this.addAccountsToCache(this.initialAssignees)
  },

  computed: {
    labelText() {
      return this.label ?? this.$t('common.assignee_other')
    },

    isSearching() {
      return _.isEmpty(this.searchTerm)
    },

    selectedAccounts() {
      if (!this.multiple) {
        return [this.cachedAccounts[this.selectedAccountIds]]
      }

      return this.selectedAccountIds.map(
        accountId => this.cachedAccounts[accountId]
      )
    },

    inputItems() {
      const accounts = _.uniqBy(
        [...this.availableAccounts, ...this.selectedAccounts],
        'id'
      )

      return _.orderBy(
        [
          ...this.parseAccounts(_.compact(accounts)),
          ...this.parseTeams(this.availableTeams),
        ],
        item => item.title.toLowerCase(),
        'asc'
      )
    },

    rules() {
      const rules = []
      if (this.required)
        rules.push(
          items => items.length > 0 || this.$t('routines.min_assignees_error')
        )
      if (this.limit)
        rules.push(
          items =>
            items.length <= this.limit ||
            this.$t('routines.maximum_assignees', {
              maxAssignees: this.$constants.routines.assignees.maxLength,
            })
        )
      return rules
    },
  },

  methods: {
    handleChange(items) {
      this.searchTerm = ''
      if (_.isEmpty(items) || !this.multiple) return this.$emit('change', items)
      const lastItem = items.pop()
      const newItems = _.uniq([
        ...items,
        ...(lastItem?.isTeam
          ? this.getAccountIdsFromTeam(lastItem)
          : [lastItem?._id ?? lastItem?.id ?? lastItem]),
      ])
      this.$emit('change', newItems)
    },

    getAccountIdsFromTeam(team) {
      return [
        ...team.leaders?.map(leader => leader.id),
        ...team.members?.map(member => member.id),
      ]
    },

    handleRemove(accountToRemove) {
      if (!this.multiple) return this.$emit('change', null)
      const nextAccounts = this.selectedAccountIds.filter(
        account => account !== accountToRemove.id
      )

      this.$emit('change', nextAccounts)
    },

    parseAccounts(accounts) {
      if (!accounts) return

      return accounts?.map?.(account => ({
        ...account,
        id: account?._id ?? account?.id,
        photo: account?.photo,
        title: this.getFullName(account),
        subtitle: account?.position,
      }))
    },

    parseTeams(teams) {
      if (!teams) return

      return teams?.map?.(team => {
        const totalMembers = this.getTeamTotalMembers(team)

        return {
          ...team,
          isTeam: true,
          title: team.name,
          subtitle: `${totalMembers} ${this.$t('common.member', {
            count: totalMembers,
          })}`,
        }
      })
    },

    getTeamTotalMembers(team) {
      return (team?.leaders?.length ?? 0) + (team?.members?.length ?? 0)
    },

    addAccountsToCache(accounts) {
      const parsedNewAccounts = this.parseAccounts(accounts)
      const hashedAccounts = _.keyBy(parsedNewAccounts, 'id')
      this.cachedAccounts = { ...this.cachedAccounts, ...hashedAccounts }
    },
  },

  watch: {
    searchTerm: _.debounce(function(term) {
      this.$emit('search', term)
    }, 1000),

    availableAccounts(newAccounts) {
      this.addAccountsToCache(newAccounts)
    },

    availableTeams(newTeams) {
      const teamAccounts = newTeams.reduce((result, team) => {
        return [...result, ...team.leaders, ...team.members]
      }, [])

      this.addAccountsToCache(teamAccounts)
    },

    initialAssignees(newAccounts) {
      this.addAccountsToCache(newAccounts)
    },
  },
}
</script>

<style lang="scss">
.account-single-name {
  text-transform: capitalize;
}

.assignee-selector .v-select__selections {
  padding-top: 10px !important;
}

.autocomplete-menu {
  max-height: 140px !important;
}
</style>
